import { useRef, useState } from "react";
import { Upload, X, Loader2, Library } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const ACCEPT_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

async function uploadOne(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data.public_url;
}

/**
 * ImageUpload — single image. Pick from local computer, drag-and-drop, or library.
 */
export function ImageUpload({ value, onChange, testIdPrefix = "image-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    const upload = async (file) => {
        if (!ACCEPT_TYPES.has(file.type)) {
            toast.error("Unsupported image type");
            return;
        }
        setUploading(true);
        try {
            const url = await uploadOne(file);
            onChange(url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) upload(file);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) upload(file);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`h-24 w-24 rounded-2xl overflow-hidden border-2 ${
                        dragOver ? "border-brand-500 bg-brand-50" : "border-dashed border-gray-300 bg-paper-alt"
                    } flex items-center justify-center shrink-0 hover:border-brand-300 transition`}
                    data-testid={`${testIdPrefix}-dropzone`}
                    aria-label="Choose or drop image"
                >
                    {value ? (
                        <img src={value} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                        <div className="flex flex-col items-center gap-1 text-ink-400">
                            <Upload className="h-5 w-5" />
                            <span className="text-[9px] uppercase tracking-wider font-semibold">
                                Drop or click
                            </span>
                        </div>
                    )}
                </button>
                <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-brand-50 text-brand-700 text-sm font-medium hover:bg-brand-100 disabled:opacity-50"
                            data-testid={`${testIdPrefix}-pick-btn`}
                        >
                            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                            {uploading ? "Uploading…" : "Choose image"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPickerOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-paper-alt text-ink-700 text-sm font-medium hover:bg-gray-200"
                            data-testid={`${testIdPrefix}-library-btn`}
                        >
                            <Library className="h-3.5 w-3.5" /> From library
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-paper-alt text-ink-700 text-sm hover:bg-gray-200"
                                data-testid={`${testIdPrefix}-clear-btn`}
                            >
                                <X className="h-3.5 w-3.5" /> Clear
                            </button>
                        )}
                    </div>
                    <Input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPT}
                        onChange={onSelect}
                        className="hidden"
                        data-testid={`${testIdPrefix}-input`}
                    />
                    <Input
                        type="url"
                        placeholder="…or paste an image URL"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="rounded-xl text-xs"
                        data-testid={`${testIdPrefix}-url-input`}
                    />
                </div>
            </div>

            <MediaLibraryPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onSelect={(url) => onChange(url)}
            />
        </div>
    );
}

/**
 * MultiImageUpload — manages a list of image URLs.
 */
export function MultiImageUpload({ value = [], onChange, testIdPrefix = "multi-image-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    const uploadFiles = async (files) => {
        setUploading(true);
        const uploaded = [];
        try {
            for (const file of files) {
                if (!ACCEPT_TYPES.has(file.type)) {
                    toast.error(`${file.name}: unsupported type`);
                    continue;
                }
                try {
                    uploaded.push(await uploadOne(file));
                } catch (err) {
                    toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
                }
            }
            if (uploaded.length > 0) {
                onChange([...(value || []), ...uploaded]);
                toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
            }
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length) uploadFiles(files);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) uploadFiles(files);
    };

    const remove = (idx) => {
        onChange((value || []).filter((_, i) => i !== idx));
    };

    return (
        <div className="space-y-3">
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`flex flex-wrap gap-3 p-2 rounded-2xl ${
                    dragOver ? "bg-brand-50 ring-2 ring-brand-300 ring-offset-1" : ""
                }`}
                data-testid={`${testIdPrefix}-dropzone`}
            >
                {(value || []).map((u, i) => (
                    <div
                        key={`${u}-${i}`}
                        className="relative h-24 w-24 rounded-2xl overflow-hidden border border-gray-200 bg-paper-alt group"
                        data-testid={`${testIdPrefix}-item-${i}`}
                    >
                        <img src={u} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 text-accent-700 flex items-center justify-center shadow-soft opacity-0 group-hover:opacity-100 transition"
                            data-testid={`${testIdPrefix}-remove-${i}`}
                            aria-label="Remove"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="h-24 w-24 rounded-2xl border-2 border-dashed border-gray-300 bg-paper-alt text-ink-700 hover:border-brand-300 hover:text-brand-600 flex flex-col items-center justify-center gap-1 transition disabled:opacity-50"
                    data-testid={`${testIdPrefix}-pick-btn`}
                >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                    <span className="text-[10px] uppercase tracking-wider font-semibold">
                        {uploading ? "Uploading" : "Add image"}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="h-24 w-24 rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-400 flex flex-col items-center justify-center gap-1 transition"
                    data-testid={`${testIdPrefix}-library-btn`}
                >
                    <Library className="h-5 w-5" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold">
                        From library
                    </span>
                </button>
            </div>
            <p className="text-[11px] text-ink-700">Drag &amp; drop multiple images here, or use the buttons above.</p>
            <Input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                onChange={onSelect}
                multiple
                className="hidden"
                data-testid={`${testIdPrefix}-input`}
            />

            <MediaLibraryPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                multiple
                onSelectMany={(urls) => onChange([...(value || []), ...urls])}
            />
        </div>
    );
}
